<template>
  <div
    role="main"
    aria-label="Tableau des délégations logistiques"
    tabindex="-1"
  >
    <!-- Dashboard metrics - Using standard B-Card components -->
    <b-row
      class="match-height mb-2"
      aria-label="Statistiques logistiques globales"
    >
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="delegation-stat-card delegation-stat-primary text-center bg-light-primary">
          <div class="delegation-stat-icon">
            <feather-icon
              icon="GridIcon"
              size="20"
              class="text-white"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalRecords || 0 }}
          </h2>
          <p class="delegation-stat-label mb-0">
            TOTAL DES DÉLÉGATIONS
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="delegation-stat-card delegation-stat-success text-center bg-light-success">
          <div class="delegation-stat-icon">
            <feather-icon
              icon="CheckCircleIcon"
              size="20"
              class="text-white"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="delegation-stat-label mb-0">
            DÉLÉGATIONS ACTIVES
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="delegation-stat-card delegation-stat-warning text-center bg-light-warning">
          <div class="delegation-stat-icon">
            <feather-icon
              icon="MapPinIcon"
              size="20"
              class="text-white"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalLocalities }}
          </h2>
          <p class="delegation-stat-label mb-0">
            TOTAL LOCALITÉS
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="delegation-stat-card delegation-stat-info text-center bg-light-info">
          <div class="delegation-stat-icon">
            <feather-icon
              icon="TruckIcon"
              size="20"
              class="text-white"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalOrders }}
          </h2>
          <p class="delegation-stat-label mb-0">
            TOTAL COMMANDES
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Advanced filters -->
    <b-card
      class="mb-2"
      aria-label="Filtres avancés pour la gestion des délégations"
    >
      <b-card-title id="filters-title">
        Filtres avancés
      </b-card-title>
      <span class="sr-only">Utilisez ces filtres pour affiner la recherche des délégations selon des critères logistiques professionnels.</span>
      <b-row>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="Gouvernorat">
            <v-select
              v-model="filters.governorate"
              :options="governorateOptions"
              placeholder="Sélectionner un gouvernorat"
              :clearable="true"
              :searchable="true"
              label="name"
              class="select-search-option"
              :reduce="option => option"
              @input="getDelegations"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="État">
            <v-select
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              @input="getDelegations"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Nombre de localités">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.minLocalities"
                  v-debounce:500ms="getDelegations"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.maxLocalities"
                  v-debounce:500ms="getDelegations"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Date de création">
              <b-row>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdAfter"
                    placeholder="À partir de"
                    locale="fr"
                    class="mb-2 filter-input"
                    @input="getDelegations"
                  />
                </b-col>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdBefore"
                    placeholder="Jusqu'à"
                    locale="fr"
                    class="filter-input"
                    @input="getDelegations"
                  />
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-between mt-1">
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <span>Réinitialiser</span>
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="toggleMoreFilters"
        >
          <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- Header section with title and badge -->
    <div
      class="custom-search d-flex justify-content-between mb-2"
      aria-label="Barre d’outils de gestion des délégations"
    >
      <div class="d-flex align-items-center">
        <h3 class="mb-0 mr-2">
          Liste des Délégations
        </h3>
        <b-badge
          variant="primary"
          pill
          class="badge-round"
        >
          {{ totalRecords || 0 }}
        </b-badge>
      </div>
      <div class="d-flex">
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <b-button
            :to="{ name: 'delegation-add' }"
            variant="primary"
            class="d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter délégation</span>
          </b-button>
        </b-form-group>
      </div>
    </div>

    <!-- Loading states with skeletons -->
    <div
      v-if="isLoading"
      class="mb-2"
      aria-live="polite"
      role="status"
    >
      <b-card>
        <b-skeleton-table
          :rows="5"
          :columns="6"
          :table-props="{ bordered: true, striped: true }"
        />
        <span class="sr-only">Chargement des délégations logistiques en cours...</span>
      </b-card>
    </div>

    <!-- Enhanced table -->
    <vue-good-table
      v-else
      ref="delegationsTable"
      :columns="columns"
      :rows="delegations"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
        mode: 'records'
      }"
      :total-rows="totalRecords"
      :is-loading="isLoading"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'name', type: 'asc'}
      }"
      style-class="vgt-table bordered striped hover responsive"
      table-class="delegations-table"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Enhanced delegation name column with link -->
        <span v-if="props.column.field === 'name'">
          <div class="d-flex align-items-center">
            <div class="delegation-name">
              <router-link
                :to="{ name: 'delegation-detail', params: { id: props.row.id }}"
                class="font-weight-bold text-primary"
              >
                {{ props.formattedRow[props.column.field] }}
              </router-link>
            </div>
          </div>
        </span>

        <!-- Enhanced governorate column with link -->
        <span v-else-if="props.column.field === 'governorate.name'">
          <router-link
            v-if="props.row.governorate && props.row.governorate.id"
            v-b-tooltip.hover
            :to="{ name: 'governorate-detail', params: { id: props.row.governorate.id }}"
            title="Voir détails du gouvernorat"
          >
            {{ props.formattedRow[props.column.field] }}
          </router-link>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </span>

        <!-- Enhanced status column with animation -->
        <span v-else-if="props.column.field === 'is_active'">
          <div class="status-cell">
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="props.row.is_active === true"
                key="active"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-success mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="CheckIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Activé</span>
                </b-badge>
              </div>
              <div
                v-else
                key="inactive"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-danger mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Désactivé</span>
                </b-badge>
              </div>
            </transition>
          </div>
        </span>

        <!-- Enhanced action column -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <router-link
              v-b-tooltip.hover
              class="text-info mr-1"
              :to="{ name: 'delegation-detail', params: { id: props.row.id }}"
              title="Voir détails"
            >
              <feather-icon
                icon="EyeIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <router-link
              v-if="isAdmin"
              v-b-tooltip.hover
              class="text-warning mr-1"
              :to="{ name: 'delegation-edit', params: { id: props.row.id }}"
              title="Modifier"
            >
              <feather-icon
                icon="EditIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <b-button
              v-if="isAdmin"
              v-b-tooltip.hover
              variant="link"
              class="btn-icon p-0"
              :disabled="toggleStatusLoading === props.row.id"
              :title="props.row.is_active ? 'Désactiver cette délégation' : 'Activer cette délégation'"
              @click="confirmToggleStatus(props.row.id, !props.row.is_active)"
            >
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
              />
              <feather-icon
                v-else
                :icon="props.row.is_active ? 'XIcon' : 'CheckIcon'"
                :class="props.row.is_active ? 'text-danger' : 'text-success'"
                size="18"
              />
            </b-button>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Empty state template -->
      <template slot="emptystate">
        <div
          class="empty-state text-center py-5"
          aria-live="polite"
          role="status"
        >
          <feather-icon
            icon="GridIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h4>Aucune délégation logistique trouvée</h4>
          <p class="text-muted">
            {{ searchTerm ? 'Aucune délégation logistique correspondant à votre recherche.' : 'Aucune délégation logistique disponible.' }}
          </p>
          <div v-if="hasActiveFilters">
            <p>Essayez de modifier vos filtres ou réinitialisez-les pour élargir la recherche.</p>
            <b-button
              variant="outline-primary"
              size="sm"
              aria-label="Réinitialiser les filtres logistiques"
              @click="resetFilters"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap d-none d-sm-inline">
              Affichage
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} résultat{{ props.total > 1 ? 's' : '' }} trouvé{{ props.total > 1 ? 's' : '' }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BFormDatepicker,
  BModal,
  BSkeletonTable,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BFormDatepicker,
    BModal,
    vSelect,
    BSkeletonTable,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Délégation',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
        },
        {
          label: 'Gouvernorat',
          field: 'governorate.name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
        },
        {
          label: 'N° localités',
          field: 'num_localities',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher nombre de localités',
          },
          thClass: 'text-center min-width-100',
          tdClass: 'text-center min-width-100',
          sortable: true,
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
          sortable: true,
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
        },
      ],
      delegations: [],
      searchTerm: '',
      showMoreFilters: false,
      totalRecords: 0,
      currentPage: 1,
      toggleStatusLoading: null,
      delegationToToggle: null,
      newStatus: null,
      statusModalTitle: '',
      statusModalMessage: '',

      // Advanced filters
      filters: {
        name: '',
        governorate: null,
        active: null,
        minLocalities: null,
        maxLocalities: null,
        createdAfter: null,
        createdBefore: null,
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
      governorateOptions: [],

      // Add statistics variables
      activeCount: 0,
      totalLocalities: 0,
      totalOrders: 0,
    }
  },
  computed: {
    isAdmin() {
      return this.user && this.user.role === 'admin'
    },
    hasActiveFilters() {
      return (
        this.filters.governorate !== null
        || this.filters.active !== null
        || this.filters.minLocalities !== null
        || this.filters.maxLocalities !== null
        || this.filters.createdAfter !== null
        || this.filters.createdBefore !== null
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.loadGovernorateOptions()
    this.getDelegations()
  },
  methods: {
    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    async loadGovernorateOptions() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorateOptions = data || []
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des gouvernorats')
        this.governorateOptions = []
      }
    },

    // Get delegations with applied filters
    async getDelegations() {
      this.isLoading = true
      try {
        let url = '/api/delegations/detailed/'

        // Add query params for filters
        const params = new URLSearchParams()
        if (this.filters.name) params.append('name__icontains', this.filters.name)
        if (this.filters.governorate) params.append('governorate', this.filters.governorate.id)
        if (this.filters.active !== null) params.append('is_active', this.filters.active)

        // Add new filter params
        if (this.filters.minLocalities !== null && this.filters.minLocalities !== '') {
          params.append('min_localities', parseInt(this.filters.minLocalities, 10))
        }
        if (this.filters.maxLocalities !== null && this.filters.maxLocalities !== '') {
          params.append('max_localities', parseInt(this.filters.maxLocalities, 10))
        }

        // Add date filters with proper formatting
        if (this.filters.createdAfter) {
          params.append('created_after', this.formatDate(this.filters.createdAfter))
        }
        if (this.filters.createdBefore) {
          params.append('created_before', this.formatDate(this.filters.createdBefore))
        }

        // Add pagination params
        params.append('limit', this.pageLength)
        params.append('offset', (this.currentPage - 1) * this.pageLength)

        // Append params to URL if any exist
        if (params.toString()) url = `${url}?${params.toString()}`

        const { data } = await axios.get(url)
        // Handle paginated response from backend
        if (data.results) {
          this.delegations = data.results
          this.totalRecords = data.count
        } else {
          this.delegations = data
          this.totalRecords = data.length
        }

        // Calculate statistics after data load
        this.$nextTick(() => {
          this.calculateStatistics()
        })
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des délégations')
      } finally {
        this.isLoading = false
      }
    },

    // Calculate statistics
    calculateStatistics() {
      this.activeCount = this.delegations.filter(d => d.is_active).length
      this.totalLocalities = this.delegations.reduce((sum, d) => sum + (d.num_localities || 0), 0)
      // For demonstration, assuming each delegation has a num_orders property
      this.totalOrders = this.delegations.reduce((sum, d) => sum + (d.num_orders || 0), 0)
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        name: '',
        governorate: null,
        active: null,
        minLocalities: null,
        maxLocalities: null,
        createdAfter: null,
        createdBefore: null,
      }
      this.getDelegations()
    },

    // Status toggle methods
    confirmToggleStatus(id, makeActive) {
      this.delegationToToggle = id
      this.newStatus = makeActive
      this.statusModalTitle = makeActive ? 'Activer la délégation' : 'Désactiver la délégation'
      this.statusModalMessage = makeActive
        ? 'Êtes-vous sûr de vouloir activer cette délégation ?'
        : 'Êtes-vous sûr de vouloir désactiver cette délégation ?'
      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.delegationToToggle = null
      this.newStatus = null
    },

    handleStatusConfirmed() {
      if (this.delegationToToggle !== null) {
        this.toggleStatus(this.delegationToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/delegations/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the delegations array to avoid a full reload
        const index = this.delegations.findIndex(delegation => delegation.id === id)
        if (index !== -1) {
          this.delegations[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `La délégation a été ${makeActive ? 'activée' : 'désactivée'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.delegationToToggle = null
        this.newStatus = null
      }
    },

    // Pagination methods
    onPageChange(params) {
      this.currentPage = params.currentPage
      this.getDelegations()
    },

    onPerPageChange(params) {
      this.pageLength = params.currentPerPage
      this.getDelegations()
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import "@core/scss/vue/libs/vue-select.scss";

// Enhanced table styles
.delegations-table {
  .vgt-table {
    border-collapse: separate;
    border-spacing: 0;

    th {
      position: relative;
      font-weight: 600;

      &.active-sorting {
        background-color: rgba($color: #7367f0, $alpha: 0.1);
        color: #7367f0;
      }

      &.sorting-header {
        cursor: pointer;

        &:hover {
          background-color: rgba($color: #7367f0, $alpha: 0.05);
        }
      }
    }

    td {
      vertical-align: middle;
    }
  }

  // Status badges
  .badge-glow {
    &.bg-success {
      box-shadow: 0 0 10px rgba(40, 199, 111, 0.6);
    }

    &.bg-danger {
      box-shadow: 0 0 10px rgba(234, 84, 85, 0.6);
    }
  }
}

// Responsive table styles
.vgt-table {
  min-width: 800px;

  @media (max-width: 992px) {
    .min-width-200 {
      min-width: 150px;
    }

    .min-width-150 {
      min-width: 120px;
    }

    .min-width-100 {
      min-width: 80px;
    }
  }

  @media (max-width: 768px) {
    .min-width-200 {
      min-width: 120px;
    }

    .min-width-150 {
      min-width: 100px;
    }

    .min-width-100 {
      min-width: 60px;
    }
  }
}

// Improve button styling in action column
.btn-icon {
  padding: 0.25rem 0.5rem;

  @media (max-width: 576px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.8rem;
  }
}

// Improved input field styling
.form-control, .vs__dropdown-toggle, .filter-input {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #B50100;
    box-shadow: none;
  }
}

// Custom styles for v-select component
.select-search-option {
  .vs__dropdown-toggle {
    padding: 8px 0;
    border-color: #e0e0e0;
    background-color: #fff;
    border-radius: 4px;
  }

  .vs__selected {
    padding: 0 6px;
  }

  .vs__search {
    padding: 0 8px;
  }

  .vs__dropdown-menu {
    border-radius: 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .vs--open .vs__dropdown-toggle {
    border-color: #B50100;
  }
}

// Badge styling to match the image
.badge-round {
  padding: 0.3rem 0.65rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: #B50100 !important;
}

// Match button styling from image
.btn-primary {
  background-color: #B50100 !important;
  border-color: #B50100 !important;
}

// Add styles for statistic cards
.card {
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #5e5873;
  }

  .text-center {
    min-height: 120px;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  &.bg-light-primary {
    background-color: rgba($color: #7367f0, $alpha: 0.12) !important;
  }

  &.bg-light-success {
    background-color: rgba($color: #28c76f, $alpha: 0.12) !important;
  }

  &.bg-light-warning {
    background-color: rgba($color: #ff9f43, $alpha: 0.12) !important;
  }

  &.bg-light-info {
    background-color: rgba($color: #00cfe8, $alpha: 0.12) !important;
  }
}

.delegation-stat-card {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.06);

  .card-body {
    padding: 1.35rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .delegation-stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.9rem;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
  }

  h2 {
    font-size: 2rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.35rem;
    line-height: 1;
  }

  .delegation-stat-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
  }
}

.delegation-stat-primary {
  .delegation-stat-icon {
    background: linear-gradient(135deg, #7367f0 0%, #5f55ee 100%);
  }

  .delegation-stat-label {
    color: #7367f0;
  }
}

.delegation-stat-success {
  .delegation-stat-icon {
    background: linear-gradient(135deg, #28c76f 0%, #1fb565 100%);
  }

  .delegation-stat-label {
    color: #28c76f;
  }
}

.delegation-stat-warning {
  .delegation-stat-icon {
    background: linear-gradient(135deg, #ff9f43 0%, #ff851a 100%);
  }

  .delegation-stat-label {
    color: #ff9f43;
  }
}

.delegation-stat-info {
  .delegation-stat-icon {
    background: linear-gradient(135deg, #00cfe8 0%, #00b8cf 100%);
  }

  .delegation-stat-label {
    color: #00cfe8;
  }
}

// Empty state styling
.empty-state {
  padding: 3rem 1rem;

  svg {
    opacity: 0.6;
  }
}

// Animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
